import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import i18next from 'i18next';
import { Batch } from '@/types/cbs';
import { useCBSBatchStore } from '@/stores/cbsBatchStore';
import { getAuthenticatedClient, getCurrentUserId } from '@/services/client';
import { toast } from '@/hooks/use-toast';

// Fetch active batch for farmer
export const useActiveBatch = (farmerId: string) => {
  const { setActiveBatch } = useCBSBatchStore();

  return useQuery<Batch | null>({
    queryKey: ['cbs', 'batch', 'active', farmerId],
    queryFn: async () => {
      const client = getAuthenticatedClient();
      const { data, error } = await client
        .from('batches')
        .select('*')
        .eq('user_id', farmerId)
        .eq('batch_status', 'active')
        .order('placement_date', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      if (data) setActiveBatch(data as Batch);
      return (data as Batch) ?? null;
    },
    staleTime: 1000 * 60 * 5,
    retry: 2,
    retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 5000),
    enabled: !!farmerId,
  });
};

// Fetch batch by ID
export const useBatch = (batchId: string) => {
  const { getBatchById } = useCBSBatchStore();

  return useQuery({
    queryKey: ['cbs', 'batch', batchId],
    queryFn: async () => {
      const client = getAuthenticatedClient();
      const { data, error } = await client
        .from('batches')
        .select('*')
        .eq('id', batchId)
        .single();
      if (error) throw error;
      return data;
    },
    retry: 2,
    retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 5000),
    initialData: getBatchById(batchId),
    enabled: !!batchId,
  });
};

// Fetch batch history for farmer
export const useBatchHistory = (farmerId: string) => {
  const { batches } = useCBSBatchStore();

  return useQuery({
    queryKey: ['cbs', 'batches', farmerId],
    queryFn: async () => {
      try {
        const client = getAuthenticatedClient();
        const { data, error } = await client
          .from('batches')
          .select('*')
          .eq('user_id', farmerId)
          .order('placement_date', { ascending: false });
        if (error) throw error;
        return data ?? [];
      } catch {
        return batches;
      }
    },
    staleTime: 1000 * 60 * 10,
    retry: 2,
    retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 5000),
    initialData: batches,
    enabled: !!farmerId,
  });
};

// Create new batch
export const useCreateBatch = () => {
  const queryClient = useQueryClient();
  const { addBatch } = useCBSBatchStore();

  return useMutation({
    mutationFn: async (input: Omit<Batch, 'id' | 'created_at' | 'updated_at' | 'status_changed_at'>) => {
      const client = getAuthenticatedClient();
      const { data, error } = await client
        .from('batches')
        .insert(input)
        .select()
        .single();
      if (error) throw error;
      return data;
    },

    onMutate: async (newBatch) => {
      await queryClient.cancelQueries({ queryKey: ['cbs', 'batch'] });
      const previousBatches = queryClient.getQueryData<Batch[]>(['cbs', 'batches', newBatch.user_id]);

      const optimisticBatch: Batch = {
        ...newBatch,
        id: `temp-${Date.now()}`,
        current_bird_count: newBatch.doc_count,
        batch_status: 'placed',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        status_changed_at: new Date().toISOString(),
      };

      queryClient.setQueryData<Batch[]>(['cbs', 'batches', newBatch.user_id], (old = []) => [
        optimisticBatch,
        ...old,
      ]);
      addBatch(optimisticBatch);
      return { previousBatches };
    },

    onError: (error, variables, context) => {
      if (context?.previousBatches) {
        queryClient.setQueryData(['cbs', 'batches', variables.user_id], context.previousBatches);
      }
      toast({ title: i18next.t("cbs.batch.createFailed", { defaultValue: "Failed to create batch" }), description: error instanceof Error ? error.message : i18next.t("common.unknownError", { defaultValue: "Unknown error" }), variant: 'destructive' });
    },

    onSuccess: () => {
      toast({ title: i18next.t("cbs.batch.created", { defaultValue: "Batch started" }), description: i18next.t("cbs.batch.createdDesc", { defaultValue: "Batch created successfully" }) });
    },

    onSettled: (_, __, variables) => {
      queryClient.invalidateQueries({ queryKey: ['cbs', 'batch'] });
      queryClient.invalidateQueries({ queryKey: ['cbs', 'batches', variables.user_id] });
    },
  });
};

// Update batch
export const useUpdateBatch = () => {
  const queryClient = useQueryClient();
  const { updateBatch } = useCBSBatchStore();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Batch> }) => {
      const client = getAuthenticatedClient();
      const { error } = await client.from('batches').update(data).eq('id', id);
      if (error) throw error;
    },

    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey: ['cbs', 'batch'] });
      updateBatch(id, data);
      return {};
    },

    onError: (error) => {
      toast({ title: i18next.t("cbs.batch.updateFailed", { defaultValue: "Failed to update batch" }), description: error instanceof Error ? error.message : i18next.t("common.unknownError", { defaultValue: "Unknown error" }), variant: 'destructive' });
    },

    onSuccess: () => {
      toast({ title: i18next.t("cbs.batch.updated", { defaultValue: "Batch updated" }), description: i18next.t("common.changesSaved", { defaultValue: "Your changes have been saved" }) });
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['cbs', 'batch'] });
    },
  });
};

// End batch (harvest)
export const useEndBatch = () => {
  const queryClient = useQueryClient();
  const { endBatch } = useCBSBatchStore();

  return useMutation({
    mutationFn: async ({ id, actualHarvestDate }: { id: string; actualHarvestDate: string }) => {
      const client = getAuthenticatedClient();
      const { data, error } = await client
        .from('batches')
        .update({ batch_status: 'completed', actual_harvest_date: actualHarvestDate })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },

    onMutate: async ({ id, actualHarvestDate }) => {
      await queryClient.cancelQueries({ queryKey: ['cbs', 'batch'] });
      endBatch(id, actualHarvestDate);
      return {};
    },

    onError: (error) => {
      toast({ title: i18next.t("cbs.batch.endBatchFailed"), description: error instanceof Error ? error.message : i18next.t("common.unknownError", { defaultValue: "Unknown error" }), variant: 'destructive' });
    },

    onSuccess: () => {
      toast({ title: i18next.t("cbs.batch.completedToast"), description: i18next.t("cbs.batch.harvestedDesc", { defaultValue: "Batch has been harvested" }) });
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['cbs', 'batch'] });
    },
  });
};